import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, formatDistance } from 'date-fns';
import toast from 'react-hot-toast';
import client from '../../../lib/feathers';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import UserAvatar from '../../../components/UserAvatar';
import UserDeletionModal from '../../../components/UserDeletionModal';
import ContactTimeline from '../../../components/ContactTimeline';
import EmailEditor from '../../../components/EmailEditor';
import TemplatePicker from '../../../components/TemplatePicker';

// Visit reason display mapping
const visitReasonLabels = {
  wine_tasting: 'Wine Tasting',
  event: 'Event',
  dining: 'Dining',
  tour: 'Tour',
};

// Rating display mapping (-1 = frown, 0 = meh, 1 = smile, 2 = heart eyes)
const ratingDisplay = {
  '-1': { emoji: '😞', label: 'Disliked' },
  '0': { emoji: '😐', label: 'Meh' },
  '1': { emoji: '😊', label: 'Liked' },
  '2': { emoji: '😍', label: 'Loved' },
};

// Stat Card Component
const StatCard = ({ label, value, icon, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  };

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`flex-shrink-0 rounded-md p-3 ${colorClasses[color]}`}>
            {icon}
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
              <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

// Small colored stat tile for the drinking-window summary in the cellar card.
const DrinkingWindowStat = ({ label, value, color }) => (
  <div className={`rounded-lg px-3 py-2 ${color}`}>
    <div className="text-xl font-semibold leading-tight">{value || 0}</div>
    <div className="text-xs">{label}</div>
  </div>
);

// Ranked list for type / varietal / country mix in the cellar card.
const BreakdownList = ({ title, items }) => {
  const list = Array.isArray(items) ? items : [];
  const total = list.reduce((sum, i) => sum + (i.count || 0), 0);
  if (list.length === 0) {
    return (
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
        <p className="text-sm text-gray-400">No data</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      <ul className="space-y-1">
        {list.map((entry) => {
          const pct = total > 0 ? Math.round((entry.count / total) * 100) : 0;
          return (
            <li key={entry.name} className="flex items-center justify-between text-sm">
              <span className="text-gray-700 truncate pr-2">{entry.name}</span>
              <span className="text-gray-500 whitespace-nowrap">
                {entry.count} <span className="text-gray-400">({pct}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const accessLevelOptions = [
  { value: 'free', label: 'Free' },
  { value: 'taster', label: 'Taster ($1.99/mo)' },
  { value: 'collector', label: 'Collector ($4.99/mo)' },
  { value: 'enthusiast', label: 'Enthusiast ($9.99/mo)' },
  { value: 'connoisseur', label: 'Connoisseur ($19.99/mo)' },
  { value: 'vip', label: 'VIP ($29.99/mo)' },
];

const accessLevelColors = {
  free: 'bg-gray-100 text-gray-800',
  taster: 'bg-green-100 text-green-800',
  collector: 'bg-blue-100 text-blue-800',
  enthusiast: 'bg-purple-100 text-purple-800',
  connoisseur: 'bg-yellow-100 text-yellow-800',
  vip: 'bg-red-100 text-red-800',
};

export default function UserDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userListings, setUserListings] = useState([]);
  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState();
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userDataCounts, setUserDataCounts] = useState(null);
  const [editingAccess, setEditingAccess] = useState(false);
  const [newAccessLevel, setNewAccessLevel] = useState(null);
  const [editingOverride, setEditingOverride] = useState(false);
  const [overrideLevel, setOverrideLevel] = useState(null);
  const [overrideExpiry, setOverrideExpiry] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [subscriptionEvents, setSubscriptionEvents] = useState([]);
  const [libraryImports, setLibraryImports] = useState([]);

  // CRM section state (Phase 4a). The history endpoint also returns the
  // user's outreachContact rows so the panel can show "Contact: active"
  // vs "unsubscribed" / "bounced".
  const [messageHistory, setMessageHistory] = useState([]);
  const [contactInfo, setContactInfo] = useState([]);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [emailVariables, setEmailVariables] = useState([]);
  const [sending, setSending] = useState(false);
  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'industry', label: 'Industry' },
    { value: 'admin', label: 'Admin' },
  ];
  const listingRoleOptions = [
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff' },
  ];
  const [newListing, setNewListing] = useState();
  const [newRelationship, setNewRelationship] = useState();
  const [newEmail, setNewEmail] = useState('');
  const [formKey, setFormKey] = useState(0);

  const setNewRoleFromUser = (u) => {
    setNewRole({ value: u.role, label: u.role });
  };

  const getUser = async (id) => {
    try {
      setLoading(true);
      const service = client.service('admin/user-detail');
      const result = await service.get(id);
      setUser(result);
      setNewRoleFromUser(result);

      // Fetch subscription events if user has Stripe data
      if (result.stripeCustomerId || result.stripeSubscriptionId) {
        try {
          const eventService = client.service('admin/subscription-event-log');
          const events = await eventService.find({ query: { userId: id, $sort: { createdAt: -1 }, $limit: 20 } });
          setSubscriptionEvents(events.data || []);
        } catch (err) {
          console.log('Error loading subscription events:', err);
        }
      }

      // Library imports — quietly fetch (best-effort; missing service shouldn't
      // break the page for users without admin-library-import deployed yet).
      try {
        const importService = client.service('admin/library-import');
        const imports = await importService.find({ query: { userId: id, $limit: 10 } });
        setLibraryImports(imports.data || []);
      } catch (err) {
        // ignore — section just hides when empty
      }

      // CRM: email history + contact rows.
      try {
        const historyService = client.service('admin/user-message-history');
        const history = await historyService.find({ query: { userId: id, $limit: 20 } });
        setMessageHistory(history.data || []);
        setContactInfo(history.contacts || []);
      } catch (err) {
        // ignore — section just hides when empty
      }
    } catch (e) {
      console.log('error', e);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (role) => {
    const service = client.service('admin/user');
    await service.patch(user.id, { role: role.value });
    await getUser(user.id);
    setNewRole(role);
    setEditingRole(false);
  };

  const updateAccessLevel = async () => {
    if (!newAccessLevel) return;
    try {
      const service = client.service('admin/user');
      await service.patch(user.id, { accessLevel: newAccessLevel.value });
      toast.success(`Access level updated to ${newAccessLevel.label}`);
      await getUser(user.id);
      setEditingAccess(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update access level');
    }
  };

  const saveOverride = async () => {
    try {
      const service = client.service('admin/user');
      const overrideData = overrideLevel ? {
        level: overrideLevel.value,
        expiresAt: overrideExpiry || null,
        reason: overrideReason || null,
      } : null;
      await service.patch(user.id, { accessOverride: overrideData });
      toast.success(overrideData ? 'Override saved' : 'Override removed');
      await getUser(user.id);
      setEditingOverride(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update override');
    }
  };

  const clearOverride = async () => {
    try {
      const service = client.service('admin/user');
      await service.patch(user.id, { accessOverride: null });
      toast.success('Override removed');
      await getUser(user.id);
      setEditingOverride(false);
    } catch (err) {
      toast.error(err.message || 'Failed to clear override');
    }
  };

  const initOverrideEdit = () => {
    const existing = user.accessOverride;
    if (existing) {
      setOverrideLevel(accessLevelOptions.find((o) => o.value === existing.level) || null);
      setOverrideExpiry(existing.expiresAt ? existing.expiresAt.split('T')[0] : '');
      setOverrideReason(existing.reason || '');
    } else {
      setOverrideLevel(null);
      setOverrideExpiry('');
      setOverrideReason('');
    }
    setEditingOverride(true);
  };

  const loadListings = async (input, callback) => {
    const service = client.service('admin/listing');
    const q = {
      $sort: { name: 1 }, // ASC - alphabetical order for type-ahead
      name: { $iLike: `%${input}%` },
      isExcluded: false,
    };
    const result = await service.find({ query: q });
    callback(result.data.map((l) => ({ value: l.id, label: l.name })));
  };

  const loadUserListings = async () => {
    const service = client.service('/admin/listing-user');
    const uls = await service.find({ query: { userId: uid } });
    setUserListings(uls.data);
  };

  const saveNewRelationShip = async () => {
    const data = {
      listingId: newListing,
      userId: user.id,
      email: newEmail,
      access: newRelationship,
    };
    const service = client.service('/admin/listing-user');
    await service.create(data);
    loadUserListings();
    setNewListing(null);
    setNewEmail('');
    setNewRelationship(null);
    setFormKey((prevKey) => prevKey + 1);
  };

  const onDeleteConnectionClick = async (id) => {
    const resp = confirm('Are you sure you want to delete this connection?'); // eslint-disable-line no-restricted-globals
    if (resp) {
      const service = client.service('admin/listing-user');
      await service.remove(id);
    }
    loadUserListings();
  };

  const fetchUserDataCounts = () => {
    // Compute counts from existing user stats
    const counts = {
      checkins: stats?.checkin_count || 0,
      sips: stats?.sip_count || 0,
      lists: stats?.list_count || 0,
      attachments: stats?.attachment_count || 0,
      friendships: stats?.friend_count || 0,
      friendInvitations: 0, // Not in stats
      listingAccess: userListings?.length || 0,
      listingInvitations: 0, // Not in stats
      passportParticipations: 0, // Not in stats
      photoReports: 0, // Not in stats
      groupCheckins: 0, // Not in stats
      suggestions: 0, // Not in stats
      referrals: stats?.referral_count || 0,
      totalRecords:
        (stats?.checkin_count || 0) +
        (stats?.sip_count || 0) +
        (stats?.list_count || 0) +
        (stats?.attachment_count || 0) +
        (stats?.friend_count || 0) +
        (userListings?.length || 0) +
        (stats?.referral_count || 0),
    };
    setUserDataCounts(counts);
  };

  const onDeleteAccountClick = () => {
    fetchUserDataCounts();
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (deletionType, confirmUsername) => {
    try {
      const service = client.service('admin/user');
      const result = await service.remove(uid, {
        query: { deletionType, confirmUsername },
      });

      setShowDeleteModal(false);

      if (deletionType === 'soft') {
        toast.success('User account soft deleted successfully');
      } else {
        toast.success('User account permanently deleted');
      }

      // Navigate back to users list
      setTimeout(() => {
        navigate('/admin/users');
      }, 1000);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete user account');
    }
  };

  useEffect(() => {
    if (uid) {
      getUser(uid);
    }
  }, [uid]);

  useEffect(() => {
    if (user && user.role === 'industry') loadUserListings();
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    client.service('admin/email-variable-catalog').find({})
      .then((r) => { if (!cancelled) setEmailVariables(r.data || []); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatDistance(new Date(dateStr), new Date(), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Page title="User">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading user details...</div>
        </div>
      </Page>
    );
  }

  if (!user) {
    return (
      <Page title="User">
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">User not found</div>
        </div>
      </Page>
    );
  }

  const { stats } = user;

  return (
    <Page title="User">
      <div className="space-y-6">
        {/* Header */}
        <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
          <Breadcrumb elements={[{ name: 'Users', href: '/admin/users' }, { name: `${user.firstName} ${user.lastName}` }]} />
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <StatCard
            label="Check-ins"
            value={stats?.checkin_count || 0}
            color="red"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
          <StatCard
            label="Wine Ratings"
            value={stats?.sip_count || 0}
            color="purple"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            }
          />
          <StatCard
            label="Lists"
            value={stats?.list_count || 0}
            color="blue"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
          <StatCard
            label="Friends"
            value={stats?.friend_count || 0}
            color="green"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
          <StatCard
            label="Referrals"
            value={stats?.referral_count || 0}
            color="yellow"
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            }
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Personal Information */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <UserAvatar user={user} size="lg" />
                  <div className="ml-4">
                    <p className="text-xl font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                  </div>
                </div>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-900">{user.email}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Timezone</dt>
                    <dd className="text-sm text-gray-900">{user.locale || 'Not set'}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-sm text-gray-500">Role</dt>
                    {!editingRole ? (
                      <dd className="text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' ? 'bg-red-100 text-red-800' :
                          user.role === 'industry' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                        <button onClick={() => setEditingRole(true)} className="ml-2 text-xs text-gray-500 hover:text-gray-700">Edit</button>
                      </dd>
                    ) : (
                      <dd className="flex items-center space-x-2">
                        <Select
                          options={roleOptions}
                          value={newRole}
                          onChange={(r) => setNewRole(r)}
                          className="text-sm w-32"
                        />
                        <button onClick={() => updateUserRole(newRole)} className="text-xs bg-primary text-white px-2 py-1 rounded">Save</button>
                        <button onClick={() => { setNewRoleFromUser(user); setEditingRole(false); }} className="text-xs text-gray-500">Cancel</button>
                      </dd>
                    )}
                  </div>
                  {/* Access Level */}
                  <div className="flex justify-between items-center">
                    <dt className="text-sm text-gray-500">Access Level</dt>
                    {!editingAccess ? (
                      <dd className="text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${accessLevelColors[user.accessLevel] || 'bg-gray-100 text-gray-800'}`}>
                          {accessLevelOptions.find((o) => o.value === user.accessLevel)?.label || user.accessLevel}
                        </span>
                        <button onClick={() => { setNewAccessLevel(accessLevelOptions.find((o) => o.value === user.accessLevel)); setEditingAccess(true); }} className="ml-2 text-xs text-gray-500 hover:text-gray-700">Edit</button>
                      </dd>
                    ) : (
                      <dd className="flex items-center space-x-2">
                        <Select
                          options={accessLevelOptions}
                          value={newAccessLevel}
                          onChange={(v) => setNewAccessLevel(v)}
                          className="text-sm w-44"
                        />
                        <button onClick={updateAccessLevel} className="text-xs bg-primary text-white px-2 py-1 rounded">Save</button>
                        <button onClick={() => setEditingAccess(false)} className="text-xs text-gray-500">Cancel</button>
                      </dd>
                    )}
                  </div>

                  {/* Access Override */}
                  <div className="flex justify-between items-start">
                    <dt className="text-sm text-gray-500">Override</dt>
                    {!editingOverride ? (
                      <dd className="text-sm text-right">
                        {user.accessOverride ? (
                          <div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${accessLevelColors[user.accessOverride.level] || 'bg-gray-100 text-gray-800'}`}>
                              ↑ {user.accessOverride.level}
                            </span>
                            {user.accessOverride.expiresAt && (
                              <p className="text-xs text-gray-400 mt-1">
                                Expires: {formatDate(user.accessOverride.expiresAt)}
                              </p>
                            )}
                            {user.accessOverride.reason && (
                              <p className="text-xs text-gray-400">{user.accessOverride.reason}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                        <button onClick={initOverrideEdit} className="ml-2 text-xs text-gray-500 hover:text-gray-700">Edit</button>
                      </dd>
                    ) : (
                      <dd className="text-sm">
                        <div className="space-y-2">
                          <Select
                            options={accessLevelOptions}
                            value={overrideLevel}
                            onChange={(v) => setOverrideLevel(v)}
                            placeholder="Override level..."
                            isClearable
                            className="text-sm w-44"
                          />
                          <input
                            type="date"
                            value={overrideExpiry}
                            onChange={(e) => setOverrideExpiry(e.target.value)}
                            placeholder="Expiry date"
                            className="block w-full text-sm border-gray-300 rounded-md shadow-sm"
                          />
                          <input
                            type="text"
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="Reason (e.g., beta tester)"
                            className="block w-full text-sm border-gray-300 rounded-md shadow-sm"
                          />
                          <div className="flex space-x-2">
                            <button onClick={saveOverride} className="text-xs bg-primary text-white px-2 py-1 rounded">Save</button>
                            {user.accessOverride && (
                              <button onClick={clearOverride} className="text-xs bg-red-600 text-white px-2 py-1 rounded">Remove</button>
                            )}
                            <button onClick={() => setEditingOverride(false)} className="text-xs text-gray-500">Cancel</button>
                          </div>
                        </div>
                      </dd>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Member Since</dt>
                    <dd className="text-sm text-gray-900">{formatDate(user.createdAt)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Last Updated</dt>
                    <dd className="text-sm text-gray-900">{formatRelativeDate(user.updatedAt)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Stripe Subscription */}
            {(user.stripeCustomerId || user.stripeSubscriptionId) && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Stripe Subscription</h3>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd className="text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.stripeSubscriptionStatus === 'active' ? 'bg-green-100 text-green-800'
                            : user.stripeSubscriptionStatus === 'past_due' ? 'bg-yellow-100 text-yellow-800'
                              : user.stripeSubscriptionStatus === 'paused' ? 'bg-gray-100 text-gray-800'
                                : user.stripeSubscriptionStatus === 'disputed' ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-600'
                        }`}>
                          {user.stripeSubscriptionStatus || 'No subscription'}
                        </span>
                      </dd>
                    </div>
                    {user.stripePriceId && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Plan</dt>
                        <dd className="text-sm text-gray-900">{user.stripePriceId}</dd>
                      </div>
                    )}
                    {user.stripeCurrentPeriodEnd && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Renews</dt>
                        <dd className="text-sm text-gray-900">{formatDate(user.stripeCurrentPeriodEnd)}</dd>
                      </div>
                    )}
                    {user.stripeCustomerId && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-500">Stripe Customer</dt>
                        <dd className="text-sm">
                          <a
                            href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {user.stripeCustomerId}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Subscription Event Log */}
                  {subscriptionEvents.length > 0 && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Event Log</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {subscriptionEvents.map((evt, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-gray-400 whitespace-nowrap">{format(new Date(evt.createdAt), 'MMM d, h:mm a')}</span>
                            <span className="text-gray-700">{evt.summary}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Referral Information */}
            {(user.referrer || (stats?.referral_count > 0)) && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Referral Information</h3>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  {user.referrer && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">Referred By</p>
                      <Link to={`/admin/users/${user.referrer.id}`} className="text-red-600 hover:text-red-800">
                        {user.referrer.firstName} {user.referrer.lastName}
                        <span className="text-gray-400 ml-1">@{user.referrer.username}</span>
                      </Link>
                    </div>
                  )}
                  {user.recentReferrals && user.recentReferrals.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Recent Referrals ({stats?.referral_count})</p>
                      <ul className="space-y-2">
                        {user.recentReferrals.map((ref) => (
                          <li key={ref.id} className="text-sm">
                            <Link to={`/admin/users/${ref.id}`} className="text-red-600 hover:text-red-800">
                              {ref.firstName} {ref.lastName}
                            </Link>
                            <span className="text-gray-400 ml-2">{formatRelativeDate(ref.createdAt)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cellar / Library */}
            {user.cellarStats && user.cellarStats.total_entries > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Cellar ({user.cellarStats.total_bottles} bottles · {user.cellarStats.unique_wines || 0} unique wines)
                    </h3>
                    <div className="text-sm text-gray-500">
                      {user.cellarStats.total_locations} location{user.cellarStats.total_locations !== 1 ? 's' : ''}
                      {user.cellarStats.last_added && (
                        <> · last added {formatDistance(new Date(user.cellarStats.last_added), new Date(), { addSuffix: true })}</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-5 sm:p-6 space-y-6">

                  {/* Drinking Window summary */}
                  {user.cellarDrinkingWindow && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Drinking window</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        <DrinkingWindowStat label="Past peak" value={user.cellarDrinkingWindow.past_peak} color="bg-red-50 text-red-700" />
                        <DrinkingWindowStat label="Nearing end" value={user.cellarDrinkingWindow.nearing_end} color="bg-amber-50 text-amber-700" />
                        <DrinkingWindowStat label="In peak" value={user.cellarDrinkingWindow.peak} color="bg-green-50 text-green-700" />
                        <DrinkingWindowStat label="Too early" value={user.cellarDrinkingWindow.too_early} color="bg-blue-50 text-blue-700" />
                        <DrinkingWindowStat label="Unknown" value={user.cellarDrinkingWindow.unknown} color="bg-gray-100 text-gray-600" />
                      </div>
                    </div>
                  )}

                  {/* Locations */}
                  {user.cellarLocations && user.cellarLocations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Locations</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {user.cellarLocations.map((loc) => (
                          <div key={loc.id} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-sm text-gray-900">{loc.name}</div>
                              {!loc.is_owner && (
                                <span className="text-[10px] uppercase tracking-wide text-gray-400 ml-2">shared</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {loc.bottle_count} bottle{loc.bottle_count !== 1 ? 's' : ''} · {loc.entry_count} entr{loc.entry_count !== 1 ? 'ies' : 'y'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mix breakdown — 3 columns */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <BreakdownList title="By type" items={user.cellarByType} />
                    <BreakdownList title="Top varietals" items={user.cellarByVarietal} />
                    <BreakdownList title="Top countries" items={user.cellarByCountry} />
                  </div>
                </div>
              </div>
            )}

            {/* Library Imports */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Library Imports
                  {libraryImports.length > 0 && (
                    <span className="ml-2 text-sm text-gray-500">({libraryImports.length})</span>
                  )}
                </h3>
                <Link
                  to={`/admin/users/${user.id}/library-import`}
                  className="text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Import Library →
                </Link>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {libraryImports.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No imports yet. Use <strong>Import Library</strong> to upload a CellarTracker CSV.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="py-2 pr-3">When</th>
                        <th className="py-2 pr-3">Filename</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Rows</th>
                        <th className="py-2 pr-3">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {libraryImports.map((imp) => (
                        <tr
                          key={imp.id}
                          className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                          onClick={() => { window.location.href = `/admin/library-imports/${imp.id}`; }}
                        >
                          <td className="py-2 pr-3">{new Date(imp.createdAt).toLocaleString()}</td>
                          <td className="py-2 pr-3">{imp.filename || '—'}</td>
                          <td className="py-2 pr-3">{imp.status}</td>
                          <td className="py-2 pr-3">{imp.summary?.rowCount ?? '—'}</td>
                          <td className="py-2 pr-3">{imp.summary?.createdEntries ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Friends */}
            {user.friends && user.friends.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Friends ({user.friends.length})</h3>
                </div>
                <div className="px-4 py-5 sm:p-6 max-h-96 overflow-y-auto">
                  <ul className="space-y-3">
                    {user.friends.map((friend) => (
                      <li key={friend.friendship_id} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <UserAvatar user={friend} size="sm" />
                          <div className="ml-3">
                            <Link to={`/admin/users/${friend.id}`} className="text-sm font-medium text-red-600 hover:text-red-800">
                              {friend.firstName} {friend.lastName}
                            </Link>
                            <p className="text-xs text-gray-500">@{friend.username}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            friend.status === 'blocked' ? 'bg-red-100 text-red-800' :
                            friend.status === 'muted' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {friend.status}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">{formatRelativeDate(friend.createdAt)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Friend Requests */}
            {((user.friendRequestsReceived && user.friendRequestsReceived.length > 0) ||
              (user.friendRequestsSent && user.friendRequestsSent.length > 0)) && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Friend Requests</h3>
                </div>
                <div className="px-4 py-5 sm:p-6 max-h-96 overflow-y-auto">
                  {user.friendRequestsReceived && user.friendRequestsReceived.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Received ({user.friendRequestsReceived.length})</p>
                      <ul className="space-y-2">
                        {user.friendRequestsReceived.map((req) => (
                          <li key={req.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <UserAvatar user={req} size="sm" className="flex-shrink-0" />
                              <div className="ml-2">
                                <Link to={`/admin/users/${req.user_id}`} className="text-sm text-red-600 hover:text-red-800">
                                  {req.firstName} {req.lastName}
                                </Link>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                req.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {req.status}
                              </span>
                              <span className="text-xs text-gray-400">{formatRelativeDate(req.createdAt)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {user.friendRequestsSent && user.friendRequestsSent.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Sent ({user.friendRequestsSent.length})</p>
                      <ul className="space-y-2">
                        {user.friendRequestsSent.map((req) => (
                          <li key={req.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <UserAvatar user={req} size="sm" className="flex-shrink-0" />
                              <div className="ml-2">
                                <Link to={`/admin/users/${req.user_id}`} className="text-sm text-red-600 hover:text-red-800">
                                  {req.firstName} {req.lastName}
                                </Link>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                req.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {req.status}
                              </span>
                              <span className="text-xs text-gray-400">{formatRelativeDate(req.createdAt)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Top Visited Listings */}
            {user.topListings && user.topListings.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Favorite Spots</h3>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  <ul className="space-y-3">
                    {user.topListings.map((listing) => (
                      <li key={listing.listing_id} className="flex justify-between items-start">
                        <div>
                          <Link to={`/admin/listings/${listing.listing_id}`} className="text-sm font-medium text-red-600 hover:text-red-800">
                            {listing.listing_name}
                          </Link>
                          <p className="text-xs text-gray-500">{listing.listing_city}, {listing.listing_state}</p>
                        </div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {listing.visit_count} visits
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            <div className="bg-white shadow rounded-lg border-2 border-red-200">
              <div className="px-4 py-5 sm:px-6 border-b border-red-200 bg-red-50">
                <h3 className="text-lg font-medium text-red-900">Danger Zone</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Delete this account</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Permanently remove this user account and all associated data, or mark as deleted.
                    </p>
                  </div>
                  <button
                    onClick={onDeleteAccountClick}
                    className="ml-4 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Check-ins */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Check-ins</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {user.recentCheckins && user.recentCheckins.length > 0 ? (
                  <div className="space-y-4">
                    {user.recentCheckins.map((checkin) => (
                      <div key={checkin.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <Link to={`/admin/listings/${checkin.listing_id}`} className="font-medium text-red-600 hover:text-red-800">
                              {checkin.listing_name}
                            </Link>
                            {checkin.visitReason && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {visitReasonLabels[checkin.visitReason] || checkin.visitReason}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{checkin.listing_city}, {checkin.listing_state}</p>
                          {checkin.notes && (
                            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{checkin.notes}</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-sm text-gray-500">{formatRelativeDate(checkin.createdAt)}</p>
                          {checkin.sip_count > 0 && (
                            <p className="text-xs text-purple-600">{checkin.sip_count} wine{checkin.sip_count !== 1 ? 's' : ''} rated</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No check-ins yet</p>
                )}
              </div>
            </div>

            {/* Recent Wine Ratings */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Wine Ratings</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {user.recentSips && user.recentSips.length > 0 ? (
                  <div className="space-y-4">
                    {user.recentSips.map((sip) => (
                      <div key={sip.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <Link to={`/admin/libations/${sip.libation_id}`} className="font-medium text-red-600 hover:text-red-800">
                              {sip.wine_name}
                            </Link>
                            {sip.vintage && (
                              <span className="ml-2 text-sm text-gray-500">{sip.vintage}</span>
                            )}
                          </div>
                          {sip.listing_name && (
                            <p className="text-sm text-gray-500">at {sip.listing_name}</p>
                          )}
                          {sip.notes && (
                            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{sip.notes}</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          {sip.rating !== null && ratingDisplay[String(sip.rating)] && (
                            <div className="flex items-center justify-end" title={ratingDisplay[String(sip.rating)].label}>
                              <span className="text-2xl">{ratingDisplay[String(sip.rating)].emoji}</span>
                            </div>
                          )}
                          <p className="text-sm text-gray-500">{formatRelativeDate(sip.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No wine ratings yet</p>
                )}
              </div>
            </div>

            {/* CRM: Email History + Send Direct Email */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Email History</h3>
                  <p className="mt-1 text-sm text-gray-500">Direct sends, campaigns, and journey messages to this user</p>
                </div>
                <div className="flex items-center gap-3">
                  {contactInfo.map((c) => {
                    const cls = c.status === 'active' ? 'bg-green-100 text-green-800'
                      : c.status === 'unsubscribed' ? 'bg-gray-200 text-gray-700'
                        : c.status === 'bounced' ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800';
                    return (
                      <span key={c.id} className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${cls}`}>
                        Contact: {c.status}
                      </span>
                    );
                  })}
                  <button
                    onClick={() => {
                      setComposeSubject('');
                      setComposeBody('');
                      setShowComposeModal(true);
                    }}
                    className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700"
                  >
                    Send Direct Email
                  </button>
                </div>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {messageHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">No emails sent yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opens</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {messageHistory.map((m) => {
                          const source = m.campaign?.name
                            ? `Campaign: ${m.campaign.name}`
                            : m.journey?.name
                              ? `Journey: ${m.journey.name}`
                              : 'Direct';
                          const subject = m.campaign?.subject || '—';
                          const cls = m.status === 'sent' || m.status === 'delivered' ? 'bg-blue-100 text-blue-800'
                            : m.status === 'opened' ? 'bg-green-100 text-green-800'
                              : m.status === 'clicked' ? 'bg-purple-100 text-purple-800'
                                : m.status === 'bounced' || m.status === 'spam' ? 'bg-red-100 text-red-800'
                                  : m.status === 'unsubscribed' ? 'bg-gray-200 text-gray-700'
                                    : 'bg-yellow-100 text-yellow-800';
                          return (
                            <tr key={m.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {m.createdAt ? formatDistance(new Date(m.createdAt), new Date(), { addSuffix: true }) : '—'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700">{source}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{subject}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
                                  {m.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{m.openCount || 0}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{Array.isArray(m.clickedLinks) ? m.clickedLinks.length : 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* CRM: Full Activity Timeline (messages + engagement + journeys + tier changes) */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
                <p className="mt-1 text-sm text-gray-500">Email engagement, journey enrollments, and subscription history</p>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <ContactTimeline userId={user.id} refreshKey={timelineRefreshKey} />
              </div>
            </div>

            {/* Industry Connections (for industry users only) */}
            {user.role === 'industry' && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Industry Connections</h3>
                  <p className="mt-1 text-sm text-gray-500">Winery connections and relationships</p>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  {/* Add New Connection Form */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Add New Connection</p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <AsyncSelect
                        key={`listing-${formKey}`}
                        loadOptions={loadListings}
                        placeholder="Select Listing"
                        onChange={(r) => setNewListing(r.value)}
                        className="text-sm"
                      />
                      <Select
                        key={`role-${formKey}`}
                        options={listingRoleOptions}
                        placeholder="Role"
                        onChange={(r) => setNewRelationship(r.value)}
                        className="text-sm"
                      />
                      <input
                        type="email"
                        name="newEmail"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Email"
                        className="block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      />
                      <button
                        onClick={saveNewRelationShip}
                        className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Existing Connections */}
                  {userListings && userListings.length > 0 ? (
                    <div className="space-y-3">
                      {userListings.map((ul) => (
                        <div key={ul.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{ul.listing.name}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                ul.access === 'manager' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {ul.access}
                              </span>
                              <span className="text-sm text-gray-500">{ul.email}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => onDeleteConnectionClick(ul.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No connections yet</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Deletion Modal */}
      {showDeleteModal && userDataCounts && (
        <UserDeletionModal
          user={user}
          userDataCounts={userDataCounts}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Compose Direct Email Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Send Direct Email</h3>
              <p className="text-sm text-gray-600 mt-1">
                To: {user.firstName} {user.lastName} &lt;{user.email}&gt;
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <TemplatePicker onPick={(tpl) => {
                if (!tpl) return;
                setComposeSubject(tpl.subject || '');
                setComposeBody(tpl.bodyHtml || '');
              }}
              />
              <div>
                <label htmlFor="composeSubject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  id="composeSubject"
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  maxLength={200}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
                  placeholder="Quick follow-up — Hi {{firstName}}"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                <EmailEditor
                  value={composeBody}
                  onChange={setComposeBody}
                  variables={emailVariables}
                  placeholder="Write your message…"
                  minHeight="240px"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Footer with unsubscribe link is auto-appended. Sent via Postmark <code className="bg-gray-100 px-1 rounded">outbound</code> (transactional) stream. Opens / clicks track automatically.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowComposeModal(false)}
                disabled={sending}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!composeSubject.trim() || !composeBody.trim()) {
                    toast.error('Subject and body are required.');
                    return;
                  }
                  setSending(true);
                  try {
                    await client.service('admin/campaign-direct-send').create({
                      recipientUserId: user.id,
                      subject: composeSubject.trim(),
                      bodyHtml: composeBody,
                    });
                    toast.success('Email sent.');
                    setShowComposeModal(false);
                    // Refresh history so the new row appears.
                    try {
                      const historyService = client.service('admin/user-message-history');
                      const history = await historyService.find({ query: { userId: user.id, $limit: 20 } });
                      setMessageHistory(history.data || []);
                      setContactInfo(history.contacts || []);
                    } catch (err) {
                      // ignore
                    }
                    setTimelineRefreshKey((k) => k + 1);
                  } catch (err) {
                    toast.error(err.message || 'Send failed');
                  } finally {
                    setSending(false);
                  }
                }}
                disabled={sending}
                className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
